import { captureException } from "extensions/sentry";
import { isPlainObject, sortBy, uniqueId } from "lodash-es";

import { store } from "common/store";

import { getters } from "common/components/getters/getters";
import { references } from "common/helpers/references";
import { PazienError } from "common/helpers/errors";
import { getCurrencyName } from "common/helpers/currency";

const blackList = ["invoice_no", "original_cbk_date_providers"];

const rules = {
    avs_response_codes: (pivot, value) => {
        const alias = value;
        return {
            pivot: "avs_response_code",
            pivot_alias: "AVS response codes",
            alias,
            typeahead: alias,
            typeahead_alias: [alias]
        };
    },
    
    bank: (pivot, value) => rules.default_lookup(pivot, value, false),
    
    bu: (pivot, obj) => {
        const getTokens = () => {
            // BUs should be searchable by alias
            const matchRegex = /\((.*?)\)/;
            const words = getAlias().split(" ");
            const tokens = [];
            words.forEach((w) => {
                const match = w.match(matchRegex);
                if (match && match[1]) {
                    tokens.push(match[1]);
                }
            });
            return tokens.join(" ");
        };
        const getAlias = () => obj.display_name;
        
        return {
            alias: getAlias(),
            token_alias: getAlias(),
            typeahead: getTokens(),
            typeahead_alias: [getAlias()]
        };
    },
    
    card: (pivot, value) => rules.default_lookup(pivot, value, false),
    
    category: (pivot, value) => rules.default_lookup(pivot, value, false),
    
    currency_alias: (pivot, value) => {
        const alias = getCurrencyName(value);
    
        const getTokens = () => {
            // Add actual string to the tokens
            const tokens = [value];
            tokens.push(alias);
            return tokens.join(" ");
        };
        return {
            alias,
            token_alias: value,
            typeahead_alias: [alias, value],
            typeahead: getTokens()
        };
    },
    
    cycle: (pivot, value) => {
        const getAlias = () => references.toAlias(value, pivot);
        return {
            alias: getAlias(),
            token_alias: getAlias(),
            typeahead: getAlias(),
            typeahead_alias: [getAlias()]
        };
    },
    
    fee_detail: (pivot, value) => ({
        alias: value,
        token_alias: value,
        typeahead: value,
        typeahead_alias: [value]
    }),
    
    mcc: (pivot, value) => {
        const getTokens = () => {
            // Add actual string to the tokens
            const tokens = [value];
            tokens.push(getAlias());
            return tokens.join(" ");
        };
    
        const getAlias = () => {
            // eslint-disable-next-line sentence-case/sentence-case
            if (value === "Unknown MCC") {
                return value;
            }
            return `${value} - ${references.getAliasGroupByType("mcc")[value]?.description}`;
        };
        
        return {
            alias: getAlias(),
            token_alias: getAlias(),
            typeahead_alias: [getAlias()],
            typeahead: getTokens()
        };
    },
    
    mid: (pivot, value) => {
        const getTokens = () => {
            const stringSize = value.length;
            // Add actual string to the tokens
            const tokens = [value];
            // multiple combinations for every available size
            // (eg. 1234567 = 567, 4567, 34567, 234567)
            for (let size = 3; size <= Math.min(6, stringSize); size++) {
                tokens.push(value.substr(stringSize - size, stringSize));
            }
            return tokens.join(" ");
        };
        const getAlias = () => references.toAlias(value, pivot);
        return {
            alias: getAlias(),
            token_alias: getAlias(),
            typeahead_alias: [getAlias()],
            typeahead: getTokens()
        };
    },
    
    purchase_currency: (pivot, value) => {
        const alias = getCurrencyName(value);
        const getTokens = () => {
            // Add actual string to the tokens
            const tokens = [value];
            tokens.push(alias);
            return tokens.join(" ");
        };
        
        return {
            alias,
            token_alias: value,
            typeahead_alias: [alias, value],
            typeahead: getTokens()
        };
    },
    
    reason_code: (pivot, value, ref) => {
        const getTokens = () => {
            // Add actual string to the tokens
            const tokens = [value.chargeback_type];
            tokens.push(getAlias());
            return tokens.join(" ");
        };
        const getAlias = () => `${ref} - ${value.description}`;
        
        return {
            alias: getAlias(),
            token_alias: value.description,
            typeahead: getTokens(),
            typeahead_alias: [ref, getAlias(), value.chargeback_type]
        };
    },
    
    response_code: (pivot, value, ref) => {
        const getTokens = () => {
            // Add actual string to the tokens
            const tokens = [value.decline_type];
            tokens.push(getAlias());
            return tokens.join(" ");
        };
        const getAlias = () => value.description;
        
        return {
            alias: getAlias(),
            token_alias: getAlias(),
            typeahead: getTokens(),
            typeahead_alias: getAlias()
        };
    },
    
    default_lookup: (pivot, value, translate_alias = true) => {
        const alias = translate_alias ? references.toAlias(value, pivot) : value;
        return {
            alias,
            token_alias: alias,
            typeahead_alias: [alias],
            typeahead: alias
        };
    },
    
    admin: (pivot, alias, valueMeta) => ({
        alias,
        token_alias: valueMeta?.token_alias || alias,
        typeahead_alias: valueMeta?.typeahead_alias || [alias],
        typeahead: valueMeta?.typeahead || alias
    })
};

const getMappings = () => {
    const mappings = store.getState().common.mappings;
    
    const localDimensions = {
        reason_code: mappings.cbk_codes,
        response_code: mappings.decline_codes
    };
    
    if (store.getState().permissions.user_permissions.has_fraud) {
        localDimensions.psd2 = references.getAliasGroupByType("psd2");
        localDimensions["fraud_reason_code"] = references.getAliasGroupByType("fraud_reason_code");
        localDimensions.EUR_amount_bucket = references.getAliasGroupByType("EUR_amount_bucket");
        localDimensions.psd2_region = references.getAliasGroupByType("psd2_region");
    }
    
    if (store.getState().permissions.user_permissions.feature_authorizations_attempts) {
        localDimensions.auths_attempts = references.getAliasGroupByType("auths_attempts");
    }
    
    if (store.getState().permissions.user_permissions.feature_test_authorizations) {
        localDimensions.auths_tests = references.getAliasGroupByType("auths_tests");
    }
    
    if (store.getState().permissions.user_permissions.data_access_chargebacks) {
        localDimensions.cycle = mappings.cbk_lifecycle;
        localDimensions.category = references.getAliasGroupByType("category");
    }
    
    if (store.getState().permissions.user_permissions.data_access_sales_refunds) {
        localDimensions.stl_status = references.getAliasGroupByType("stl_status");
        localDimensions.stl_type = references.getAliasGroupByType("stl_type");
    }
    
    if (store.getState().permissions.user_permissions.data_access_fees) {
        localDimensions.fee_type = references.getAliasGroupByType("fee_type");
    }
    
    if (store.getState().permissions.user_permissions.data_access_authorizations) {
        localDimensions.response_type = references.getAliasGroupByType("response_type");
    }
    
    if (store.getState().permissions.user_permissions.data_access_authorizations || store.getState().permissions.user_permissions.data_access_sales_refunds) {
        localDimensions.cvv2_response_code = mappings.cvv_response_codes;
    }
    
    if (store.getState().permissions.user_permissions.data_access_authorizations || store.getState().permissions.user_permissions.data_access_sales_refunds) {
        localDimensions.avs_response_code = mappings.avs_response_codes;
    }
    
    return localDimensions;
};

// TODO: move computation to worker
export function buildSourceData({
    sources = "*", overrideMode = "replace", sourcesOverride = null, addUniqueId = true, isBenchmark = false
}) { // TODO: handle custom sources (Benchmarks, Request tool)
    let sourcesData;
    
    const computedFilterBarData = store.getState().common.filterBarData.all;
    if (computedFilterBarData && sourcesOverride === null) {
        sourcesData = computedFilterBarData;
    } else {
        const start = Date.now();
        let data;
    
        if (overrideMode === "merge") {
            data = {
                ...store.getState().common.merchant_dimensions.data,
                ...getMappings(),
                bu: references.getAliasGroupByType("bu"),
                mcc_category: references.getAliasGroupByType("mcc_category"),
                ...sourcesOverride?.data
            };
        } else {
            data = sourcesOverride?.data || {
                ...store.getState().common.merchant_dimensions.data,
                ...getMappings(),
                bu: references.getAliasGroupByType("bu"),
                mcc_category: references.getAliasGroupByType("mcc_category")
            };
        }
        
        blackList.forEach((b) => {
            delete data[b];
        });
        
        sourcesData = Object.entries(data).reduce((acc, [pivot, values]) => {
            const pivot_alias = getters.findPivotName(pivot) || sourcesOverride?.meta?.[pivot]?.pivot_alias || pivot;
            let valuesArray; // normalize to `[ [ref, alias], ... ]`
            if (Array.isArray(values)) {
                valuesArray = values.map((v) => [v, v]);
            } else if (isPlainObject(values)) {
                valuesArray = Object.entries(values);
            } else {
                return acc;
            }
          
            // Return earlier if no values available (e.g. digital_wallet on Farfetch)
            if (valuesArray.length === 0) return acc;
            // Filter out and values that have an empty Ref like description: "", “Visa - Unknown chargeback type”
            valuesArray = valuesArray.filter((v) => v[0] !== "");
            
            return {
                ...acc,
                [pivot]: sortBy(valuesArray.map(([ref, alias]) => {
                    const obj = {
                        pivot,
                        pivot_alias,
                        ref
                    };
                    
                    if (addUniqueId) {
                        Object.assign(obj, {
                            id: uniqueId("data_")
                        });
                    }
                    
                    if (sourcesOverride?.resource === "admin") {
                        // Ben's madness
                        Object.assign(obj, {
                            ...rules.admin(pivot, alias, sourcesOverride?.meta[pivot]?.[ref])
                        });
                    } else if (rules[pivot]) {
                        // Pivot has a special handling
                        Object.assign(obj, {
                            ...rules[pivot]?.(pivot, alias, ref)
                        });
                    } else {
                        // Pivot has no special handling. Apply default rule
                        Object.assign(obj, {
                            ...rules.default_lookup(pivot, alias)
                        });
                    }
                    
                    return obj;
                }), ["alias"])
            };
        }, Object.create(null));
        
        if (store.getState().settings.viewInspector) {
            const end = Date.now();
            const total = end - start;
            console.log(`BUILD SOURCE DATA TOOK ${total}`);
        }
    
        if (sourcesOverride === null) {
            store.dispatch({
                type: "SET_FILTERBAR_SRCDATA",
                data: sourcesData
            });
        }
    }
    
    const checkBenchmark = (data) => {
        if (isBenchmark && data?.mcc) {
            // We cannot allow a merchant to benchmark against any individual MCC in the following ranges:
            // 3000–3299   Airlines
            // 3300–3499   Car rental
            // 3500–3999   Lodging
            data.mcc = data.mcc.filter((i) => {
                const normalized = Number.parseInt(i.ref, 10);
                return (normalized < 3000 || normalized > 3999);
            });
        }
        return data;
    };
    
    if (sources === "*") {
        return checkBenchmark({ ...sourcesData });
    }
    
    return checkBenchmark({
        ...sources.reduce((memo, source) => {
            try {
                if (Array.isArray(source) && Array.isArray(sourcesData[source[0]])) {
                    // Replace name of source[0] with source[1], e.g. "currency_alias" to "stl_currency"
                    return {
                        ...memo,
                        [source[1]]: sourcesData[source[0]].map((v) => ({
                            ...v,
                            pivot: source[1]
                        }))
                    };
                }
        
                // https://sentry.io/organizations/pazien/issues/1856114924/?project=149293&referrer=slack
                if (Array.isArray(sourcesData[source])) {
                    return {
                        ...memo,
                        [source]: sourcesData[source]
                    };
                }
        
                return memo;
            } catch (error) {
                captureException(new PazienError({
                    message: "Unable to match a source",
                    extra: { source },
                    originalError: error
                }));
                return memo;
            }
        }, {})
    });
}
